import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(isSameOrBefore)

import { useSessionStore } from '../../lib/Store/sessionStore.component'
import { nouvelleDemandeModeActions } from './NouvelleDemande.actions'
import { DocumentPickerResult } from '../../components/DocumentPicker/DocumentPicker.types'
import { DemandeAbsenceApi } from '../../api/DemandeAbsence'
import { usePolling } from '../../hooks/usePolling/usePolling.hook'

import {
  NouvelleDemande,
  NatureAbsence,
  NouvelleDemandeModeNavigation,
  NouvelleDemandeModeRoute,
  DocumentData,
  DocumentInfo
} from './NouvelleDemande.types'

import { NotifyType } from '../../components/Notification/Notification.types'
import { DotationEsag } from '../../api/Dotation/types'
import { MarkedDates } from 'react-native-calendars/src/types'

export const useNouvelleDemandeState = (
  navigation: NouvelleDemandeModeNavigation,
  route: NouvelleDemandeModeRoute,
  notify?: NotifyType
) => {
  const {currentLogin} = useSessionStore.getState().getCurrentInfos();

  // État initial d'une absence
  const initialAbsenceState: NouvelleDemande = useMemo(() => ({
    identifiant: 0,
    natureCode: '',
    natureAbsence: '',
    dateDebut: null,
    dateFin: null,
    heureDebut: null,
    heureFin: null,
    prevision: false,
    apresMidi: false,
    matin: false,
    nomFichier: '',
    contentFichier: '',
    commentaire: '',
    refus: '',
    statut: 2,
    valide: 0,
    duree: '',
    typeProfil: 1,
  }), [])

  // États principaux
  const [absence, setAbsence] = useState<NouvelleDemande>(initialAbsenceState)
  const [unenatureAbsence, setNatureAbsence] = useState<NatureAbsence | null>(null)
  const [dotation, setDotation] = useState<DotationEsag | null>(null)

  const [naturesAbsences, setNaturesAbsences] = useState<NatureAbsence[]>([])
  const [visibleDotation, setVisibleDotation] = useState<number>(0)
  const [showCalendar, setShowCalendar] = useState<boolean>(false)
  const [hourToChange, setHourToChange] = useState<Dayjs | null>(null)
  const [markedDates, setMarkedDates] = useState<MarkedDates>({})
  const [routeParamsProcessed, setRouteParamsProcessed] = useState<boolean>(false)
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [isLoadingNatures, setIsLoadingNatures] = useState<boolean>(false)

  // États pour la gestion des documents
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null)
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo | null>(null)
  const [isUploadingDocument, setIsUploadingDocument] = useState<boolean>(false)
  const [documentError, setDocumentError] = useState<string | null>(null)
  const [deleteDocument, setDeleteDocument] = useState<boolean>(false)

  // Traitement des paramètres vocaux (voiceParams)
  const [voiceParamsProcessed, setVoiceParamsProcessed] = useState<boolean>(false)

  const naturesAbsencesLoadedRef = useRef(false)
  const notifyRef = useRef(notify)

  // Charger les infos du document si on a un identifiant et qu'on est en mode modification
  // Utiliser un flag pour éviter les appels répétés
  const [documentInfoLoaded, setDocumentInfoLoaded] = useState<number | null>(null)

  const [maxDate, setMaxDate] = useState<string | undefined>(undefined)

  const getLibelle = new Map([
    ['-1-1', 'Annulée - Demande supprimée après validation non passée'],
    ['-10', 'Refusée - Demande refusée'],
    ['-11', 'Refusée - Demande supprimée (RH)'],
    ['00', 'Nouvelle demande'],
    ['11', 'Validée - Demande validée'],
    // '2*' : demande en cours de saisie
  ])

  const key = `${absence.statut || 0}${absence.valide || 0}`
  const displayText = getLibelle.get(key) || 'Nouvelle demande'

  // Callbacks simples
  const openCalendar = useCallback(() => setShowCalendar(true), [])
  const closeCalendar = useCallback(() => setShowCalendar(false),[])
  const openHourPicker = useCallback((hour: Dayjs) => () => setHourToChange(hour), [])
  const handleFilePicked = (result: DocumentPickerResult) => {
    console.log('Fichier sélectionné:', result);
    setAbsence(prev => ({ ...prev, nomFichier: result.name || '', contentFichier: result.uri || '' }));
  }
  const handleFileError = (error: Error) => notify?.error(`Erreur: ${error.message}`, 3000)
  const handleDroit = () => setVisibleDotation(prev => prev === 1 ? 0 : 1)
  const togglePrevision = () => setAbsence(prev => ({ ...prev, prevision: !prev.prevision }))

  // Gestion de la sélection de la nature d'absence
  // Chargement des natures d'absence avec la logique efficace de handleNatureAbsenceClick
  const loadNaturesAbsencesIfNeeded = useCallback(async () => {
    try {

      // Si on a des données dans le hook, les copier localement
      if (naturesAbsences.length > 0) {
        return Promise.resolve(naturesAbsences)
      }

      if (!currentLogin?.identifiantEntiteDefautEsag) {
        return Promise.resolve([])
      }

      setIsLoadingNatures(true)

      // Chargement direct des données
      const directData = await nouvelleDemandeModeActions.getNaturesAbsences(currentLogin?.identifiantEntiteDefautEsag, undefined)

      if (directData.length > 0) {
        // Stocker dans les deux états pour éviter les futures requêtes
        setNaturesAbsences(directData)
        naturesAbsencesLoadedRef.current = true
      }

      setIsLoadingNatures(false)
      return Promise.resolve(directData)

    } catch (error) {
      console.error('❌ Erreur lors du chargement des natures d\'absence:', error)
      setIsLoadingNatures(false)
      naturesAbsencesLoadedRef.current = false
      return Promise.reject(error)
    }
  }, [currentLogin?.identifiantEntiteDefautEsag, naturesAbsences])

  // Fonction pour récupérer la dotation
  const handleDotation = useCallback(async (code: string) => {
    try {
      if (currentLogin?.identifiant && currentLogin?.identifiantEntiteDefautEsag){
        const annee = dayjs().get('year')
        const result = await nouvelleDemandeModeActions.getDotation(
          currentLogin.identifiant,
          currentLogin.identifiantEntiteDefautEsag,
          code,
          annee,
          notifyRef.current
        )

        if (result) {
          setDotation(result)
          setVisibleDotation(1)
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de la dotation:", error)
    }
  }, [currentLogin?.identifiantEntiteDefautEsag, currentLogin?.identifiant])

  // Sélection de la nature d'absence
  const handleNatureAbsence = useCallback((nature: NatureAbsence) => {
    setNatureAbsence(nature)
    setAbsence(prev => ({ ...prev, natureAbsence: nature.value, natureCode: nature.code }))
    // Récupérer la dotation pour cette nature d'absence
    handleDotation(nature.code)
  }, [handleDotation])



  // Gestion de la période d'absence
  // Fonction pour chercher les heures selon les plages horaires
  const handleChercheHeure = useCallback(async (type: number, date: dayjs.Dayjs, filtre?: boolean) => {
    try {
      if (currentLogin?.identifiant){
        let mode: string | undefined
        let debut: boolean | undefined

        if (type === 1) {
          // dateDebut
          if (filtre) {
            mode = 'apresmidi'
          } else {
            debut = true
          }
        } else {
          // dateFin
          if (filtre) {
            mode = 'matin'
          }
        }

        const result = await nouvelleDemandeModeActions.getPlagesHoraires(
          currentLogin.identifiant,
          date.format('YYYYMMDD'),
          mode,
          debut,
          notifyRef.current
        )

        if (result && result.length > 0) {
          const plageData = result[0]
          let heures: number, minutes: number

          if (type === 1) {
            heures = Math.floor(plageData.heureDebut)
            minutes = Math.round((plageData.heureDebut % 1) * 100)
          } else {
            heures = Math.floor(plageData.heureFin)
            minutes = Math.round((plageData.heureFin % 1) * 100)
          }

          return dayjs(date)
            .set('hour', heures)
            .set('minute', minutes)
            .set('second', 0)
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des heures:", error)
    }
    return null
  }, [currentLogin?.identifiant])

  // Fonction pour gérer la sélection de date depuis le calendrier
  const handleDateSelection = useCallback(async (day: any) => {
    const selectedDate = dayjs(day.dateString).startOf('day')
    const heureDebut = await handleChercheHeure(1, selectedDate, absence.matin)
    const heureFin = await handleChercheHeure(2, selectedDate, absence.apresMidi)
    setAbsence(prev => {
      if(prev.dateDebut && selectedDate.isBefore(prev.dateDebut, 'day')){
        return ({
          ...prev,
          dateDebut: selectedDate,
          heureDebut: heureDebut || selectedDate,
        })
      } else if(prev.dateDebut && selectedDate.isAfter(prev.dateFin, 'day')) {
        return ({
          ...prev,
          dateFin: selectedDate,
          heureFin: heureFin || dayjs(selectedDate).endOf('day'),
        })
      } else {
        return ({
          ...prev,
          dateDebut: dayjs(selectedDate),
          dateFin: dayjs(selectedDate),
          heureDebut: heureDebut || dayjs(selectedDate),
          heureFin: heureFin || dayjs(selectedDate).endOf('day'),
        })
      }
    })

  }, [absence, handleChercheHeure])

  // Fonction pour calculer la durée via l'API
  const calculerDureeAbsence = useCallback(async (
    dateDebut: dayjs.Dayjs,
    dateFin: dayjs.Dayjs,
    heureDebut: dayjs.Dayjs,
    heureFin: dayjs.Dayjs,
    natureAbsence: string | number
  ) => {
    try {
      if (currentLogin?.identifiant){
        const result = await nouvelleDemandeModeActions.calculerDuree(
          currentLogin.identifiant,
          dateDebut.format('YYYY-MM-DD'),
          dateFin.format('YYYY-MM-DD'),
          heureDebut.hour() + (heureDebut.minute()/100),
          heureFin.hour() + (heureFin.minute()/100),
          typeof natureAbsence === 'string' ? parseInt(natureAbsence, 10) : natureAbsence,
          notifyRef.current
        )

        if (result){
          setAbsence(prev => ({ ...prev, duree: result.texte, typeProfil: result.profil }))
        }
      }
    } catch (error) {
      console.error("Erreur lors du calcul de durée:", error)
    }
  }, [currentLogin?.identifiant])



  // Gestion des heures de début et de fin
  const toggleApresMidi = useCallback(async () => {
    if (absence.dateDebut) {
      const heureDef = await handleChercheHeure(1, absence.dateDebut, !absence.apresMidi)
      setAbsence(prev => ({
        ...prev,
        heureDebut: heureDef || prev.heureDebut,
        apresMidi: !prev.apresMidi
      }))
    } else {
      setAbsence(prev => ({ ...prev, apresMidi: !prev.apresMidi }))
    }
  }, [absence.dateDebut, absence.apresMidi, handleChercheHeure])

  const toggleMatin = useCallback(async () => {
    if (absence.dateFin) {
      const heureDef = await handleChercheHeure(2, absence.dateFin, !absence.matin)
      setAbsence(prev => ({
        ...prev,
        heureFin: heureDef || prev.heureFin,
        matin: !prev.matin
      }))
    } else {
      setAbsence(prev => ({ ...prev, matin: !prev.matin }))
    }
  }, [absence.dateFin, absence.matin, handleChercheHeure])

  // Fonction pour gérer le changement d'heure
  const handleTimeChange = useCallback((currentHour: Dayjs) => (_: any, selectedDate?: Date) => {
    setHourToChange(null)

    if (selectedDate) {
      const dateJS = dayjs(selectedDate)

      switch (currentHour) {
        case absence.heureDebut:
          if(absence.dateDebut?.isSame(absence.dateFin, 'day') && absence.heureFin?.isBefore(dateJS)){
            setAbsence(prev => ({ ...prev, heureDebut: prev.heureFin, heureFin: dateJS }))
          } else {
            setAbsence(prev => ({ ...prev, heureDebut: dateJS }))
          }
          break
        case absence.heureFin:
          if(absence.dateDebut?.isSame(absence.dateFin, 'day') && absence.heureDebut?.isAfter(dateJS)){
            setAbsence(prev => ({ ...prev, heureDebut: dateJS, heureFin: prev.heureDebut }))
          } else {
            setAbsence(prev => ({ ...prev, heureFin: dateJS }))
          }
          break
        default:
          break
      }
    }
  }, [absence])



  // Gestion des documents
  const handleDocumentSelect = useCallback((document: DocumentData) => {
    setSelectedDocument(document)
    setDocumentError(null)
  }, [])

  const handleDocumentRemove = useCallback(() => {
    setDeleteDocument(true)
    setSelectedDocument(null)
    setDocumentInfo(null)
    setDocumentError(null)
  }, [])

  const handleDocumentDownload = useCallback(async (identifiant: number, docInfo: DocumentInfo) => {
    if (identifiant) {
      try {
        setIsUploadingDocument(true)
        await nouvelleDemandeModeActions.telechargerDocument(identifiant, docInfo, notify)
      } catch (error) {
        setDocumentError('Erreur lors du téléchargement du document')
        console.error('Erreur téléchargement document:', error)
      } finally {
        setIsUploadingDocument(false)
      }
    }
  }, [notify])

  const loadDocumentInfo = useCallback(async (identifiant: number) => {
    if (!identifiant) return

    try {
      const info = await nouvelleDemandeModeActions.getDocumentInfo(identifiant, notify)
      setDocumentInfo(info)
      setDocumentError(null)
    } catch (error) {
      console.error('Erreur chargement info document:', error)
      setDocumentInfo(null)
      setDocumentError(null)
    }
  }, [notify])



  // Gestion de la validation et de l'annulation/suppression

  const handleAnnule = useCallback(async () => {
    if (absence.identifiant === 0 || absence === null) {
      // Annuler, rien faire car il s'agit d'une création de demande d'absence
      navigation.navigate('HomeTabs', { screen: 'Calendrier' })
      return
    }

    // On est sur une suppression de demande
    try {
      await nouvelleDemandeModeActions.supprimerDemande(absence.identifiant, notify)

      navigation.navigate('HomeTabs', { screen: 'Calendrier' })

    } catch (error) {
      console.error("Erreur lors de la suppression de la demande d'absence:", error)
    }
  }, [absence, navigation, notify])

  const handleValide = useCallback(async () => {
    if (!currentLogin) {
      notify?.error('Session utilisateur non trouvée', 3000)
      return
    }

    if (!absence.dateDebut || !absence.dateFin || !absence.heureDebut || !absence.heureFin || !absence.natureAbsence) {
      notify?.error('Veuillez remplir tous les champs obligatoires', 3000)
      return
    }

    try {
      const apiData = {
        identifiant: absence.identifiant || undefined,
        identifiantDomaine: currentLogin.identifiantDomaine,
        identifiantAgent: currentLogin.identifiant,
        identifiantNatureAbsence: typeof absence.natureAbsence === 'string' ? parseInt(absence.natureAbsence, 10) : absence.natureAbsence,
        identifiantValidation: currentLogin.identifiantValidation,
        identifiantAbsence: 0,
        dateDebut: absence.dateDebut.format('YYYY-MM-DD'),
        dateFin: absence.dateFin.format('YYYY-MM-DD'),
        heureDebut: absence.heureDebut.hour() + (absence.heureDebut.minute() / 100),
        heureFin: absence.heureFin.hour() + (absence.heureFin.minute() / 100),
        observations: absence.commentaire,
        piecesjointes: 0,
        statut: absence.statut === 2 ? 0 : absence.statut,
        observationsGrh: '',
        valide: (absence.valide ? 1 : 0),
        prevision: absence.prevision ? 1 : 0,
        dateDemande: absence.dateDemande ? absence.dateDemande.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        dateValidation: absence.dateValidation ? absence.dateValidation.format('YYYY-MM-DD') : undefined,
        dateSuppression: absence.dateSuppression ? absence.dateSuppression.format('YYYY-MM-DD') : undefined
      }

      let result
      if (absence.identifiant && absence.identifiant > 0) {

        console.log('Modification de la demande existante avec ID:', apiData)
        // Modification d'une demande existante
        result = await nouvelleDemandeModeActions.modifierDemande(absence.identifiant, apiData, currentLogin.identifiantEntiteDefautEsag, selectedDocument || undefined, deleteDocument, notify)
      } else {
        // Création d'une nouvelle demande
        result = await nouvelleDemandeModeActions.creerDemande(apiData, currentLogin.identifiantEntiteDefautEsag, selectedDocument || undefined, notify)
      }

      if (result) {
          navigation.navigate('HomeTabs', { screen: 'Calendrier' })
      }
    } catch (error) {
      notify?.error('Erreur lors de la validation de la demande', 3000)
    }
  }, [absence, currentLogin, navigation, notify, selectedDocument, deleteDocument])

  useEffect(() => {
    if (absence.identifiant && absence.identifiant > 0 && documentInfoLoaded !== absence.identifiant) {
      setDocumentInfoLoaded(absence.identifiant)
      loadDocumentInfo(absence.identifiant)
    }
  }, [absence.identifiant, loadDocumentInfo, documentInfoLoaded])

  // Traitement des paramètres de route
  useEffect(() => {

    if (route.params && route.params.absence && !routeParamsProcessed) {

      const param = route.params.absence;

      setAbsence({
        ...initialAbsenceState,
        identifiant: param.identifiant,
        dateDebut: dayjs(param.dateDebut),
        dateFin: dayjs(param.dateFin),
        heureDebut: dayjs().hour(Math.floor(param.heureDebut)).minute(Math.round((param.heureDebut % 1) * 100)),
        heureFin: dayjs().hour(Math.floor(param.heureFin)).minute(Math.round((param.heureFin % 1) * 100)),
        commentaire: param.observations,
        prevision: param.prevision,
        statut: param.statut,
        valide: param.valide,
        natureAbsence: param.natureAbsence.identifiant,
        natureCode: param.natureAbsence.code,
        apresMidi: param.heureDebut > 12,
        matin: param.heureFin < 13,
      });

      setNatureAbsence({
        value: param.natureAbsence.identifiant,
        label: param.natureAbsence.libelle,
        code: param.natureAbsence.code,
        ordre: param.natureAbsence.ordre
      });

      handleDotation(param.natureAbsence.code);

      setRouteParamsProcessed(true);
    }
  }, [route.params, routeParamsProcessed, initialAbsenceState, handleDotation])

  useEffect(() => {
    const processVoiceParams = async () => {
      if (route.params?.voiceParams && !voiceParamsProcessed) {
        const { typeAbsenceCode, dateDebut, dateFin } = route.params.voiceParams;

        // Définir les dates
        const dateDebutDayjs = dayjs(dateDebut);
        const dateFinDayjs = dayjs(dateFin);

        setAbsence(prev => ({
          ...prev,
          dateDebut: dateDebutDayjs,
          dateFin: dateFinDayjs,
          heureDebut: dayjs().hour(8).minute(0),
          heureFin: dayjs().hour(17).minute(0),
        }));

        // Charger les natures d'absence et trouver celle qui correspond au code
        try {
          if (currentLogin?.identifiantEntiteDefautEsag) {
            const natures = await nouvelleDemandeModeActions.getNaturesAbsences(
              currentLogin.identifiantEntiteDefautEsag,
              undefined
            );
            if (natures && natures.length > 0) {
              setNaturesAbsences(natures);

              // Trouver la nature d'absence correspondant au code
              // Note: natures vient de l'API et contient identifiant/libelle (pas value/label)
              const natureMatch: NatureAbsence = natures.find(
                (n: any) => n.code?.toUpperCase() === typeAbsenceCode?.toUpperCase()
              ) as any;
              if (natureMatch) {
                setNatureAbsence({...natureMatch});

                setAbsence(prev => ({
                  ...prev,
                  natureAbsence: natureMatch.value,
                  natureCode: natureMatch.code,
                }));

                // Charger la dotation pour ce type directement via l'API
                if (natureMatch.code && currentLogin?.identifiant) {
                  const annee = dayjs().get('year');
                  const dotationResult = await nouvelleDemandeModeActions.getDotation(
                    currentLogin.identifiant,
                    currentLogin.identifiantEntiteDefautEsag,
                    natureMatch.code,
                    annee,
                    notifyRef.current
                  );
                  if (dotationResult) {
                    setDotation(dotationResult);
                    setVisibleDotation(1);
                  }
                }
              } else {
                console.warn(`Type d'absence "${typeAbsenceCode}" non trouvé`);
              }
            }
          }
        } catch (error) {
          console.error('Erreur lors du chargement des natures d\'absence:', error);
        }

        setVoiceParamsProcessed(true);
      }
    };

    processVoiceParams();
  }, [route.params?.voiceParams, voiceParamsProcessed, currentLogin?.identifiantEntiteDefautEsag, currentLogin?.identifiant]);


  // Effet pour recalculer la durée
  useEffect(() => {
    if (absence.natureAbsence && absence.dateDebut && absence.dateFin && absence.heureDebut && absence.heureFin) {
      calculerDureeAbsence(absence.dateDebut, absence.dateFin, absence.heureDebut, absence.heureFin, absence.natureAbsence)
    }
  }, [absence.dateDebut, absence.dateFin, absence.heureDebut, absence.heureFin, absence.natureAbsence, calculerDureeAbsence])

  // Surlignement de la période sélectionnée sur le calendrier
  useEffect(() => {
    const marked: MarkedDates = {}
    const situations = Array.isArray(currentLogin?.situations) ? currentLogin.situations : []

    if (absence.dateDebut && absence.dateFin) {
      let current = absence.dateDebut
      while (current.isSameOrBefore(absence.dateFin)) {
        const dateString = current.format('YYYY-MM-DD')
        if (current.isSame(absence.dateDebut) && current.isSame(absence.dateFin)) {
          marked[dateString] = { color: '#3b82f6', startingDay: true, endingDay: true }
        } else if (current.isSame(absence.dateDebut)) {
          marked[dateString] = { startingDay: true, color: '#3b82f6' }
        } else if (current.isSame(absence.dateFin)) {
          marked[dateString] = { endingDay: true, color: '#3b82f6' }
        } else {
          marked[dateString] = { color: '#3b82f6' }
        }
        current = current.add(1, 'day')
      }
    }

    let dateMax = dayjs()
    let haveFinNull = false;
    situations.forEach((situation) => {
      if((haveFinNull && situation.dateFin === null) || !haveFinNull){
        if(situation.dateFin === null){
          if(!(haveFinNull && dayjs(situation.dateDebut).isAfter(dateMax, 'day'))){
            dateMax = dayjs(situation.dateDebut)
          }
          haveFinNull = true;
        } else {
          if(dayjs(situation.dateFin).isAfter(dateMax, 'day')){
            dateMax = dayjs(situation.dateFin)
          }
        }
      }
    })

    if(!haveFinNull){
      setMaxDate(dateMax.format('YYYY-MM-DD'));
    }

    for(let date = dayjs(); date.isBefore(dateMax, 'day'); date = date.add(1, 'day')){
      if(!situations.find(
        (situation) => (date.isAfter(situation.dateDebut) || date.isSame(situation.dateDebut, 'day'))
          && (situation.dateFin === null || date.isBefore(dayjs(situation.dateFin).add(1, 'day')) || date.isSame(dayjs(situation.dateFin), 'day'))
      )){
        marked[date.format('YYYY-MM-DD')] = {
          ...marked[date.format('YYYY-MM-DD')],
          disabled: true,
        }
      }
    }

    setMarkedDates(marked)
  }, [absence.dateDebut, absence.dateFin, currentLogin?.situations])

  // Polling pour rafraîchir le statut de la demande (détecte validation/refus par le valideur)
  usePolling<{ statut: number, valide: number }>({
    fetchFn: async () => {
      const demandes = await DemandeAbsenceApi.loadDemandesAbsence(undefined, undefined, true)
      const found = demandes?.find((d: any) => d.identifiant === absence.identifiant)
      return found ? { statut: found.statut, valide: found.valide } : null
    },
    enabled: !!absence.identifiant && absence.identifiant !== 0,
    hasChanged: (prev, next) => prev.statut !== next.statut || prev.valide !== next.valide,
    onChanged: (next) => {
      setAbsence(prev => ({ ...prev, statut: next.statut, valide: next.valide }))
      notifyRef.current?.info('Le statut de la demande a été mis à jour', 3000)
    },
  })

  // Mettre à jour la ref notify à chaque render
  useEffect(() => {
    notifyRef.current = notify
  }, [notify])

  return {
    markedDates, absence, unenatureAbsence, dotation, naturesAbsences, visibleDotation,
    showCalendar, displayText, key, maxDate, setAbsence, handleNatureAbsence,
    toggleApresMidi, togglePrevision, toggleMatin, closeCalendar,
    handleFilePicked, handleFileError, handleDroit, handleAnnule,
    handleValide, handleTimeChange, loadNaturesAbsencesIfNeeded,
    modalVisible, setModalVisible, isLoadingNatures, setIsLoadingNatures, handleDateSelection, hourToChange,
    calculerDureeAbsence, handleChercheHeure, handleDotation,
    // États et fonctions pour la gestion des documents
    selectedDocument, setSelectedDocument, documentInfo, setDocumentInfo,
    isUploadingDocument, setIsUploadingDocument, documentError, setDocumentError,
    handleDocumentSelect, handleDocumentRemove, handleDocumentDownload, loadDocumentInfo,
    documentInfoLoaded, setDocumentInfoLoaded, openCalendar, openHourPicker
  }
}
